import {useViewportId} from "@AppBuilderLib/entities/viewport/model/useViewportId";
import {
	createToolbarCheckboxItem,
	createToolbarCommand,
} from "@AppBuilderLib/features/appbuilder/model/createToolbarItems";
import {useNotificationStore} from "@AppBuilderLib/features/notifications/model/useNotificationStore";
import {Logger} from "@AppBuilderLib/shared/lib/logger";
import Icon from "@AppBuilderLib/shared/ui/icon/Icon";
import TextWeighted from "@AppBuilderLib/shared/ui/text/TextWeighted";
import {
	ActionIcon,
	Box,
	Button,
	Flex,
	Group,
	Loader,
	MantineThemeComponent,
	Stack,
	Text,
	useProps,
} from "@mantine/core";
import {IInteractionEffect} from "@shapediver/viewer.features.interaction";
import {
	SelectionParameterValue,
	validateSelectionParameterSettings,
} from "@shapediver/viewer.session";
import type {ISelectionParameterProps} from "@shapediver/viewer.shared.types";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
	defaultPropsParameterWrapper,
	PropsParameter,
	PropsParameterComponent,
	PropsParameterWrapper,
} from "../config/propsParameter";
import type {ParameterSelectionComponentStyleProps as StyleProps} from "../config/theme/parameterSelectionComponentTheme";
import {getResetValue} from "../lib/parameterResetValue";
import {parseSelectionNames as parseNames} from "../model/interaction/parseSelectionNames";
import {resolveInteractionPresentation} from "../model/interaction/resolveInteractionPresentation";
import {useCommittedSelectionAdoption} from "../model/interaction/useCommittedSelectionAdoption";
import {useInteractionToolbarContribution} from "../model/interaction/useInteractionToolbarContribution";
import {
	clearPendingSelection,
	hasOtherPendingSelectionInScope,
	markPendingSelection,
	usePendingSelectionRegistry,
} from "../model/interaction/usePendingSelectionRegistry";
import {useSelection} from "../model/interaction/useSelection";
import {useSelectionActivationState} from "../model/interaction/useSelectionActivationState";
import {useSelectionInteractionOwnership} from "../model/interaction/useSelectionInteractionOwnership";
import {useSuspendedSelectionRestore} from "../model/interaction/useSuspendedSelectionRestore";
import {useParameterComponentCommons} from "../model/useParameterComponentCommons";
import classes from "./ParameterInteractionComponent.module.css";
import ParameterLabelComponent from "./ParameterLabelComponent";
import ParameterResetRow from "./ParameterResetRow";
import ParameterWrapperComponent from "./ParameterWrapperComponent";

type SelectionParameterProps = ISelectionParameterProps & {
	buttons?: {
		clear?: boolean;
	};
};

const getSelectionButtons = (settings: unknown) =>
	(settings as {props?: SelectionParameterProps} | undefined)?.props?.buttons;

const defaultStyleProps: StyleProps = {
	selectionColor: {
		type: "pulse",
		color: "#0d44f0",
		intensity: 1,
		pulseSpeed: 0.75,
	} as IInteractionEffect,
	availableColor: {
		type: "pulse",
		color: "#ffffff",
		intensity: 0.25,
		pulseSpeed: 0.75,
	} as IInteractionEffect,
	hoverColor: {
		type: "pulse",
		color: "#ffffff",
		intensity: 0.75,
		pulseSpeed: 1.75,
	} as IInteractionEffect,
};

type ParameterSelectionComponentPropsType = Partial<StyleProps>;

export function ParameterSelectionComponentThemeProps(
	props: ParameterSelectionComponentPropsType,
): MantineThemeComponent {
	return {
		defaultProps: props,
	};
}

/**
 * Functional component that creates a selection parameter, allowing selection of objects in the viewport.
 *
 * @returns
 */
export default function ParameterSelectionComponent(
	props: PropsParameter &
		Partial<PropsParameterWrapper> &
		Partial<ISelectionParameterProps>,
) {
	const {
		actions,
		definition,
		handleChange,
		setOnCancelCallback,
		onCancel,
		disabled,
		showReset,
		resetToDefault,
		value,
		state,
	} = useParameterComponentCommons<string>(props);

	const {namespace} = props;

	const {selectionColor, availableColor, hoverColor} = useProps(
		"ParameterSelectionComponent",
		defaultStyleProps,
		props,
	);

	const {wrapperComponent, wrapperProps} = useProps(
		"ParameterSelectionComponent",
		defaultPropsParameterWrapper,
		props,
	);

	// get the notification store
	const notifications = useNotificationStore();
	const selectionSettingsKey = JSON.stringify(definition.settings);
	const selectionColorKey = JSON.stringify(selectionColor);
	const availableColorKey = JSON.stringify(availableColor);
	const hoverColorKey = JSON.stringify(hoverColor);

	// settings validation
	const selectionProps = useMemo(() => {
		const buttons = getSelectionButtons(definition.settings);
		const result = validateSelectionParameterSettings(definition.settings);
		if (result.success) {
			const props = result.data.props as SelectionParameterProps;
			// Keep App Builder UI settings even while an older viewer validator is
			// in use and strips fields it does not yet know about.
			if (buttons) props.buttons = buttons;
			if (!props.selectionColor) props.selectionColor = selectionColor;
			if (!props.availableColor) props.availableColor = availableColor;
			if (!props.hoverColor) props.hoverColor = hoverColor;
			return props;
		} else {
			notifications.error({
				title: "Invalid Parameter Settings",
				message: `Invalid settings for Selection parameter "${definition.name}", see console for details.`,
			});
			Logger.warn(
				`Invalid settings for Selection parameter (id: "${definition.id}", name: "${definition.name}"): ${result.error}`,
			);
			return {
				selectionColor,
				availableColor,
				hoverColor,
			} as SelectionParameterProps;
		}
	}, [
		selectionSettingsKey,
		selectionColorKey,
		availableColorKey,
		hoverColorKey,
	]);

	const minimumSelection = selectionProps?.minimumSelection ?? 1;
	const maximumSelection = selectionProps?.maximumSelection ?? 1;
	// Clearing is UI-only: the empty draft can either be confirmed (optional
	// selections) or replaced by a new selection. For a single selection it is
	// pointless in case the selection is required (an empty draft can never be
	// confirmed, and picking another object replaces the selection anyway), or
	// in case the selection is reset to an empty selection after each execution
	// (see the "resetValue" setting). Unless configured explicitly, the Clear
	// button is hidden in these cases.
	const resetValue = getResetValue(definition);
	const resetsToEmptySelection =
		resetValue !== undefined &&
		actions.isValid(resetValue) &&
		parseNames(resetValue as string).length === 0;
	const clearIsPointless =
		maximumSelection === 1 &&
		(minimumSelection >= 1 || resetsToEmptySelection);
	const shouldShowClearButton =
		selectionProps.buttons?.clear ?? !clearIsPointless;
	const alwaysActive = selectionProps.activeMode === "alwaysActive";
	const presentation = resolveInteractionPresentation(
		selectionProps.presentation,
		alwaysActive,
	);
	// For alwaysActive, selection is always active.
	// For activeOnStart, selection starts active.
	// For default, selection starts inactive.
	const automaticallyActivated =
		alwaysActive || selectionProps.activeMode === "activeOnStart";
	const {
		deactivateSelection,
		effectiveSelectionActive,
		selectionRegistered,
		setOwnershipBlocked,
		setSelectionActive,
		setSuspended,
		suspended,
	} = useSelectionActivationState({alwaysActive, automaticallyActivated});

	// state for the dirty flag
	const [dirty, setDirty] = useState<boolean>(false);

	// get the viewport ID
	const {viewportId} = useViewportId();
	const selectionOwnerKey = `${namespace}-${definition.id}-${viewportId}`;

	// Parameters whose execution is managed elsewhere (e.g. by a form providing
	// custom actions, non-reactive) are not backed by the parameter store.
	const {reactive = true, customActions} = props as PropsParameterComponent;
	const storeBacked = reactive && !customActions?.execute;
	const restoreBatchSelectionRef = useRef(false);
	const skipNextAutomaticConfirmationRef = useRef(false);
	const clearedSinceLastConfirmationRef = useRef(false);
	const hasPendingSelectionRef = useRef(false);
	// Selected nodes removed by an output update are pruned from the selection.
	// A pruned committed selection is committed again without a computation:
	// the model produced the update which removed the nodes, the next execution
	// sends the pruned value. An automatic confirmation would cause a second
	// computation, e.g. for a selection whose executed node is replaced by the
	// response (a selectable "add" button). A pending draft is only pruned.
	const onPrunedSelection = useCallback(
		(names: string[]) => {
			if (!storeBacked || hasPendingSelectionRef.current) return;
			if (!actions.setCommittedValue(JSON.stringify({names}))) return;
			skipNextAutomaticConfirmationRef.current = true;
			clearedSinceLastConfirmationRef.current = false;
		},
		[actions, storeBacked],
	);

	const {
		candidateNodes,
		availableNodeNames,
		selectedNodeNames,
		setSelectedNodeNames,
		setSelectedNodeNamesAndRestoreSelection,
		requestSelectionRestore,
	} = useSelection(
		viewportId,
		selectionProps,
		effectiveSelectionActive,
		parseNames(value),
		true,
		false,
		onPrunedSelection,
	);
	useSuspendedSelectionRestore({
		suspended,
		selectedNodeNames,
		setSelectedNodeNames,
		requestSelectionRestore,
	});

	const acceptable =
		selectedNodeNames.length >= minimumSelection &&
		selectedNodeNames.length <= maximumSelection;
	// Pending and dirty state must use the same committed source. The value prop
	// can lag behind state.uiValue after a batch update, otherwise leaving a
	// phantom pending selection after Cancel.
	const committedNodeNames = parseNames(state.uiValue);
	const hasPendingSelection =
		committedNodeNames.length !== selectedNodeNames.length ||
		!committedNodeNames.every(
			(name, index) => name === selectedNodeNames[index],
		);
	hasPendingSelectionRef.current = hasPendingSelection;
	// Fixed and optional single selections (and complete selections) are
	// accepted automatically, unless another selection parameter has an
	// outstanding pending selection (see below).
	const acceptsAutomatically =
		selectedNodeNames.length === maximumSelection ||
		((minimumSelection === maximumSelection ||
			(minimumSelection === 0 && maximumSelection === 1)) &&
			acceptable);
	// Only a draft which needs a confirmation is registered as pending: a draft
	// which is accepted automatically is committed right away, it must not
	// switch the other selection parameters to confirmation mode meanwhile.
	const hasOtherPendingSelection = usePendingSelectionRegistry(
		selectionOwnerKey,
		`${namespace}-${viewportId}`,
		hasPendingSelection && !acceptsAutomatically,
	);
	// Keep the established automatic behavior unless another selection parameter
	// has an outstanding pending selection. A committed batch is not pending
	// interaction state, so it must restore the normal single-selection UI.
	const hasStoredSelection = hasOtherPendingSelection;
	const acceptImmediately = !hasStoredSelection && acceptsAutomatically;
	useEffect(() => {
		const parsed = parseNames(state.uiValue);

		// compare uiValue to selectedNodeNames
		if (
			parsed.length !== selectedNodeNames.length ||
			!parsed.every((n, i) => n === selectedNodeNames[i])
		) {
			setDirty(true);
		} else {
			setDirty(false);
		}
	}, [state.uiValue, selectedNodeNames]);

	// Whenever the parameter is committed (an execution of this component, an
	// external execution, a value defined by the model, a reject), the committed
	// value replaces the current draft, see useCommittedSelectionAdoption.
	const onAdoptCommittedSelection = useCallback(() => {
		// The adopted selection must not be confirmed automatically, and the
		// clear-related bookkeeping of the superseded draft is obsolete.
		skipNextAutomaticConfirmationRef.current = true;
		clearedSinceLastConfirmationRef.current = false;
	}, []);
	useCommittedSelectionAdoption({
		committedValue: storeBacked ? state.commitValue : undefined,
		commitRevision: state.commitRevision,
		selectedNodeNames,
		setSelectedNodeNames,
		onAdopt: onAdoptCommittedSelection,
	});

	// Once an execution triggered by this component completed, the committed
	// value is authoritative: it may differ from the executed selection, e.g.
	// when the parameter is reset after each execution (see the "resetValue"
	// setting). Adopt it into the selection. This is required in addition to
	// useCommittedSelectionAdoption, because the committed value itself may not
	// change at all (reset to the same value as before the execution).
	// Do not overwrite a pending selection when parameter definitions refresh.
	// Pending selection state is intentionally retained until Confirm, Cancel, or
	// Clear, even when another parameter triggers a computation.
	useEffect(() => {
		// The value prop can lag behind state.uiValue by a render (e.g. after a
		// reset value was committed). Only act once it caught up, otherwise the
		// stale value would be re-applied to the selection.
		if (value !== state.uiValue) return;
		const parsed = parseNames(value);
		const committed = parseNames(state.uiValue);
		const hasPendingSelection =
			committed.length !== selectedNodeNames.length ||
			!committed.every(
				(name, index) => name === selectedNodeNames[index],
			);
		if (hasPendingSelection) return;
		if (JSON.stringify(parsed) !== JSON.stringify(selectedNodeNames))
			setSelectedNodeNames(parsed);
	}, [JSON.stringify(definition), selectedNodeNames, state.uiValue, value]);

	// Batch confirmation temporarily deactivates the manager. That can emit a
	// deselection event before the new parameter value reaches this component;
	// restore the committed names once it does.
	useEffect(() => {
		if (!restoreBatchSelectionRef.current) return;
		restoreBatchSelectionRef.current = false;
		setSelectedNodeNamesAndRestoreSelection(parseNames(value));
	}, [setSelectedNodeNamesAndRestoreSelection, value]);

	/**
	 * Callback function to change the value of the parameter.
	 * This function is called when the selection is confirmed (by the user, or automatically).
	 * For non-alwaysActive, it also ends the selection process.
	 */
	const changeValue = useCallback(
		(names: string[]) => {
			if (
				alwaysActive &&
				hasOtherPendingSelectionInScope(
					selectionOwnerKey,
					`${namespace}-${viewportId}`,
				)
			) {
				// Another selection owns an unconfirmed draft. Automatic
				// always-active retries are expected here and stay silent.
				return;
			}
			if (!alwaysActive) {
				setSelectionActive(false);
			}
			const parameterValue: SelectionParameterValue = {names};

			// if the value is already the same, do not change it
			// (compare parsed names, the serialization of the committed value
			// may differ, e.g. for a reset value)
			const selectionWasCleared = clearedSinceLastConfirmationRef.current;
			clearedSinceLastConfirmationRef.current = false;
			if (
				JSON.stringify(parseNames(value)) === JSON.stringify(names) &&
				!selectionWasCleared
			)
				return;
			const serializedValue = JSON.stringify(parameterValue);
			handleChange(
				serializedValue,
				0,
				undefined,
				// A selection cleared only in the UI can legitimately submit the
				// same value again. Keep that exception scoped to selection.
				selectionWasCleared,
			);
		},
		[alwaysActive, namespace, selectionOwnerKey, value, viewportId],
	);

	// The automatic confirmation must be triggered by an actual change of the
	// selection, not by a new identity of changeValue. Otherwise a re-render
	// caused by an unrelated parameter (e.g. a dynamic parameter whose value the
	// model changes on every computation) would re-run this effect and submit
	// the unchanged selection again, causing a second computation. changeValue
	// is therefore called via a ref instead of being a dependency.
	const changeValueRef = useRef(changeValue);
	changeValueRef.current = changeValue;
	useEffect(() => {
		if (skipNextAutomaticConfirmationRef.current) {
			skipNextAutomaticConfirmationRef.current = false;
			return;
		}
		// A selection equal to the committed value has nothing to confirm (e.g. a
		// committed value adopted into the selection, or a pruned selection which
		// was committed without a computation). A cleared selection is the
		// exception, it may legitimately submit the committed (reset) value again.
		if (!hasPendingSelection && !clearedSinceLastConfirmationRef.current)
			return;
		if (acceptImmediately) changeValueRef.current(selectedNodeNames);
	}, [acceptImmediately, selectedNodeNames]);

	/**
	 * Callback function to reset the selected node names.
	 * For non-alwaysActive, it also ends the selection process.
	 */
	const resetSelection = useCallback(
		(val: string) => {
			if (!alwaysActive) {
				setSelectionActive(false);
			}
			// Cancel is a committed reset, so remove the draft marker before any
			// other always-active selection can attempt an automatic update.
			clearPendingSelection(selectionOwnerKey);
			setSelectedNodeNames(parseNames(val));
		},
		[alwaysActive, selectionOwnerKey, setSelectedNodeNames],
	);

	const resetToDefaultSelection = useCallback(() => {
		const defaultNames = parseNames(definition.defval);
		if (defaultNames.length === 0) {
			skipNextAutomaticConfirmationRef.current = true;
		}
		if (!alwaysActive) {
			setSelectionActive(false);
		}
		clearPendingSelection(selectionOwnerKey);
		setSelectedNodeNamesAndRestoreSelection(defaultNames);
		resetToDefault();
	}, [
		alwaysActive,
		definition.defval,
		resetToDefault,
		selectionOwnerKey,
		setSelectedNodeNamesAndRestoreSelection,
	]);

	/**
	 * Callback function to cancel the selection.
	 * For alwaysActive: resets to last committed value but stays enabled.
	 * For others: resets to last value and ends selection.
	 */
	const cancel = useCallback(() => {
		resetSelection(value);
	}, [resetSelection, value]);

	/**
	 * Callback function to clear the selection.
	 */
	const clearSelection = useCallback(() => {
		// Clearing is UI-only for selections with confirmation controls: optional
		// and single selections would otherwise immediately auto-confirm the
		// empty draft. An always-active selection has no confirmation controls
		// and commits every change immediately, including the clear.
		skipNextAutomaticConfirmationRef.current = !alwaysActive;
		clearedSinceLastConfirmationRef.current = true;
		// This draft must be visible to other interaction parameters before the
		// selection manager emits its clear event. Otherwise an always-active
		// selection can submit independently while this parameter is invalid.
		markPendingSelection(selectionOwnerKey, `${namespace}-${viewportId}`);
		setSelectedNodeNamesAndRestoreSelection([]);
	}, [
		alwaysActive,
		namespace,
		selectionOwnerKey,
		setSelectedNodeNamesAndRestoreSelection,
		viewportId,
	]);
	const notifyConflict = useCallback(
		(title: string, message: string) =>
			notifications.warning({title, message}),
		[notifications],
	);
	const {tryAcquireClaim, releaseInteraction, takeOverInteraction} =
		useSelectionInteractionOwnership({
			viewportId,
			namespace,
			parameterId: definition.id,
			label: definition.name,
			candidateNodes,
			alwaysActive,
			automaticallyActivated,
			selectionRegistered,
			effectiveSelectionActive,
			setSelectionActive,
			setOwnershipBlocked,
			setSuspended,
			onDisable: deactivateSelection,
			setDisableOtherParameters: actions.setDisableOtherParameters,
			onConflict: notifyConflict,
		});

	// ── Toolbar registration ────────────────────────────────────────────────
	// Register with interaction toolbar if presentation is "toolbar"
	const toolbarLabel = definition.name;
	// Fixed/optional single selections normally commit automatically. A cleared
	// auto-clear draft only exposes Confirm/Cancel when its empty value is valid.
	const hasAutomaticSelectionControls = !(
		(minimumSelection === 1 && maximumSelection === 1) ||
		(minimumSelection === 0 && maximumSelection === 1)
	);
	// Confirm/Cancel belong to the selection which is being edited: an inactive
	// selection without a draft has nothing to confirm, its controls would only
	// appear as disabled buttons next to the other selection parameters.
	const showConfirmationControls =
		hasOtherPendingSelection ||
		((effectiveSelectionActive || hasPendingSelection) &&
			!acceptImmediately &&
			(hasAutomaticSelectionControls ||
				(hasPendingSelection && minimumSelection === 0)));

	const items = [
		createToolbarCheckboxItem({
			id: `${namespace}-${definition.id}-${viewportId}-toggle`,
			label: `${toolbarLabel} (${selectedNodeNames.length}/${maximumSelection})`,
			checked: effectiveSelectionActive,
			// A suspended persistent selection cannot safely resume until the
			// exclusive viewport interaction releases it. A blocked selection,
			// however, can be manually retried, unless it is blocked because there
			// is nothing to select: a retry cannot succeed then, and a toggleable
			// row would only make the selection menu appear.
			readOnly:
				alwaysActive &&
				(effectiveSelectionActive || candidateNodes.length === 0),
			// Kept on the parameter's own checkbox row. Future parameter settings
			// can omit this action to hide Clear for that parameter. Offered only
			// while something is selected, there is nothing to clear otherwise.
			trailingAction:
				shouldShowClearButton && selectedNodeNames.length > 0
					? {
							label: `Clear ${toolbarLabel}`,
							icon: "tabler:circle-off",
							execute: clearSelection,
						}
					: undefined,
			setChecked: (checked) => {
				if (checked) {
					takeOverInteraction();
					if (tryAcquireClaim(true)) setSelectionActive(true);
				} else if (!alwaysActive) setSelectionActive(false);
			},
		}),
	];
	const commands = [];
	const serializedSelectionValue = JSON.stringify({
		names: selectedNodeNames,
	});

	// Add Confirm and Cancel buttons for multi/range selections
	if (showConfirmationControls) {
		commands.push(
			createToolbarCommand({
				id: `${namespace}-${definition.id}-${viewportId}-confirm`,
				label: "Confirm",
				icon: "tabler:check",
				aggregationId: "selection-confirm",
				order: 10,
				disabled: !dirty,
				execute: () => {
					if (!acceptable) {
						notifications.warning({
							title: "Selection cannot be confirmed",
							message: `"${definition.name}" must contain between ${minimumSelection} and ${maximumSelection} selected objects.`,
						});
						return;
					}
					changeValue(selectedNodeNames);
				},
				batchUpdate: acceptable
					? {
							namespace,
							parameterId: definition.id,
							value: serializedSelectionValue,
							prepare: () => {
								restoreBatchSelectionRef.current = true;
								if (!alwaysActive) {
									releaseInteraction();
									setSelectionActive(false);
								}
							},
						}
					: undefined,
			}),
		);
		commands.push(
			createToolbarCommand({
				id: `${namespace}-${definition.id}-${viewportId}-cancel`,
				label: "Cancel",
				icon: "tabler:x",
				aggregationId: "selection-cancel",
				order: 20,
				disabled: !dirty,
				execute: cancel,
			}),
		);
	}
	useInteractionToolbarContribution({
		id: `${namespace}-${definition.id}-${viewportId}`,
		namespace,
		viewportId,
		presentation,
		sectionId: "selection",
		order: definition.order,
		menuVisibility: "multipleToggleable",
		menu: {
			id: "runtime-interaction-selection-menu",
			label: "Selection",
			icon: "tabler:hand-finger",
		},
		items,
		commands,
	});

	// Keep this registration hook above the presentation branch. Settings can
	// switch a selection parameter between widget and toolbar presentation while
	// it is mounted, and React requires the same hook sequence in both modes.
	const onCancelCallback = useCallback(() => {
		resetSelection(state.commitValue);
	}, [resetSelection, state.commitValue]);

	useEffect(() => {
		setOnCancelCallback(() => onCancelCallback);
	}, [onCancelCallback, setOnCancelCallback]);

	// Toolbar presentation owns the complete interaction UI; do not leave an
	// inline wrapper or parameter label behind in the widget tree.
	if (presentation === "toolbar") return <></>;

	/**
	 * The content of the parameter when it is active.
	 *
	 * It contains a button to confirm the selection and a button to cancel the selection
	 * as well as the number of selected nodes and the selection constraints.
	 *
	 * The confirm button is only enabled if the selection is within the constraints.
	 * The cancel button resets the selection to the last value.
	 *
	 */
	const contentActive = (
		<Stack>
			<Group justify="space-between" className={classes.interactionMain}>
				<Flex align="center" justify="flex-start" w={"100%"}>
					<Box style={{flex: 1}}>
						<TextWeighted
							size="sm"
							fontWeight="medium"
							ta="left"
							className={classes.interactionText}
						>
							{selectionProps.prompt?.activeTitle ??
								`Currently selected: ${selectedNodeNames.length}`}
						</TextWeighted>
					</Box>
					{shouldShowClearButton && (
						<Box style={{width: "auto"}}>
							<ActionIcon
								onClick={clearSelection}
								variant={
									selectedNodeNames.length === 0
										? "light"
										: "filled"
								}
							>
								<Icon iconType={"tabler:circle-off"} />
							</ActionIcon>
						</Box>
					)}
				</Flex>
				<Flex align="center" justify="flex-start" w={"100%"}>
					<Box style={{flex: 1}}>
						<Text
							size="sm"
							fs="italic"
							ta="left"
							className={classes.interactionText}
						>
							{selectionProps.prompt?.activeText ??
								(minimumSelection === maximumSelection
									? `Select ${minimumSelection} object${minimumSelection > 1 ? "s" : ""}`
									: `Select between ${minimumSelection} and ${maximumSelection} objects`)}
						</Text>
					</Box>
					<Box style={{width: "auto"}}>
						<Loader size={28} type="dots" />
					</Box>
				</Flex>
			</Group>

			{!(
				minimumSelection === maximumSelection &&
				Object.values(availableNodeNames).length >= minimumSelection
			) && (
				<Group justify="space-between" w="100%" wrap="nowrap">
					<Button
						fullWidth={true}
						disabled={!acceptable || !dirty}
						variant="filled"
						onClick={() => changeValue(selectedNodeNames)}
					>
						<Text>Confirm</Text>
					</Button>
					<Button fullWidth={true} variant={"light"} onClick={cancel}>
						<Text>Cancel</Text>
					</Button>
				</Group>
			)}
		</Stack>
	);

	/**
	 * The content of the parameter when it is inactive.
	 *
	 * It contains a button to start the selection.
	 * Within the button, the number of selected nodes is displayed.
	 */
	const contentInactive = (
		<Button
			justify="space-between"
			fullWidth={true}
			disabled={disabled}
			className={classes.interactionButton}
			rightSection={<Icon iconType={"tabler:hand-finger"} />}
			variant={selectedNodeNames.length === 0 ? "light" : "filled"}
			onClick={() => {
				if (tryAcquireClaim(true)) setSelectionActive(true);
			}}
		>
			<Text size="sm" className={classes.interactionText}>
				{selectionProps.prompt?.inactiveTitle ??
					`Start selection (${selectedNodeNames.length})`}
			</Text>
		</Button>
	);

	return (
		<ParameterWrapperComponent
			onCancel={onCancel}
			component={wrapperComponent}
			{...wrapperProps}
		>
			<ParameterLabelComponent {...props} cancel={onCancel} />
			{definition && (
				<ParameterResetRow
					show={showReset}
					onClick={resetToDefaultSelection}
					disabled={disabled}
				>
					{effectiveSelectionActive ? contentActive : contentInactive}
				</ParameterResetRow>
			)}
		</ParameterWrapperComponent>
	);
}
